/* eslint-disable no-console */
import CryptoJS from 'crypto-js';
import sha512 from 'crypto-js/sha512';
import hmacSHA512 from 'crypto-js/hmac-sha512';
import Base64 from 'crypto-js/enc-base64';
import isEmpty from 'lodash/isEmpty';
import { CRYPTO_SECRET_KEY, ENCRYPTION_DECRYPTION_ERROR } from './config.constants';
const { AES: { encrypt, decrypt } = {}, enc: { Utf8 } = {} } = CryptoJS;
const { KEY_ENV_CONFIGS_ENCRYPTED_DATA, KEY_ENV_CONFIGS_LOADED, KEY_ENV_CONFIGS_SEGMENT, KEY_ENV_CONFIGS_TENANT_ID } = {
    KEY_ENV_CONFIGS_ENCRYPTED_DATA: 'env.configs.encrypted.data',
    KEY_ENV_CONFIGS_LOADED: 'env.configs.loaded',
    KEY_ENV_CONFIGS_SEGMENT: 'env.configs.segment',
    KEY_ENV_CONFIGS_TENANT_ID: 'env.configs.tenant.id',
};
class _ConfigManager {
    constructor() {
        this._envConfigs = {};
        this._data = new Map();
    }
    set isConfigLoaded(value) {
        this._data.set(KEY_ENV_CONFIGS_LOADED, value ?? false);
    }
    get isConfigLoaded() {
        return this._data.get(KEY_ENV_CONFIGS_LOADED) ?? false;
    }
    get isConfigAvailable() {
        return this.tenantId !== null && this.isConfigLoaded === true;
    }
    get tenantId() {
        return this._data.get(KEY_ENV_CONFIGS_TENANT_ID);
    }
    set tenantId(value) {
        this._data.set(KEY_ENV_CONFIGS_TENANT_ID, value);
    }
    get encryptedData() {
        return this._data.get(KEY_ENV_CONFIGS_ENCRYPTED_DATA);
    }
    set encryptedData(value) {
        this._data.set(KEY_ENV_CONFIGS_ENCRYPTED_DATA, value);
    }
    get segment() {
        return this._data.get(KEY_ENV_CONFIGS_SEGMENT);
    }
    set segment(value) {
        this._data.set(KEY_ENV_CONFIGS_SEGMENT, value);
    }
    setEnvConfigs(config) {
        try {
            if (config?.tenantId && config?.clientId && !isEmpty(config?.responseStr)) {
                const response = JSON.parse(config.responseStr);
                const segmentKey = `${config?.tenantId}+${config?.clientId}+${response.lastUpdated}`;
                const segment = JSON.parse(decrypt(response.segment, segmentKey).toString(Utf8) ?? '{}');
                this.segment = segment?.data;
                this.encryptedData = response.data;
                this.isConfigLoaded = true;
                Object.entries(response.envConfigs).forEach(([k, v]) => (this._envConfigs[k] = v));
                this.tenantId = config.tenantId;
            }
        }
        catch (e) {
            console.error(`Failed to set envConfigs, reason: `, e);
            throw new Error(`Failed to set envConfigs, reason: ${e.message}`);
        }
    }
    config(key) {
        const config = this._envConfigs[key];
        if (config) {
            if (config.encrypted) {
                try {
                    const hashDigest = sha512(this.tenantId);
                    const encryptionKey = Base64.stringify(hmacSHA512(hashDigest, this.segment));
                    const plainEncryptionKey = decrypt(this.encryptedData, encryptionKey).toString(Utf8);
                    return decrypt(config.value, plainEncryptionKey)?.toString(Utf8);
                    //WARNING: DO NOT DELETE FOLLOWING 10 LINES
                    // const newEncKey = 'mockEncKey';
                    // const encryptedValue = encrypt('mockValue', newEncKey)?.toString();
                    // const clearValue1 = decrypt(encryptedValue, newEncKey)?.toString(UTF8);
                    //
                    // const hashDigest1 = sha512('mockTenantId');
                    // const encryptionKey1 = Base64.stringify(hmacSHA512(hashDigest1, this.segment));
                    // const encryptedEncryptionKey = encrypt(newEncKey, encryptionKey1).toString();
                    //
                    // const plainEncryptionKey1 = decrypt(encryptedEncryptionKey, encryptionKey1).toString(UTF8);
                    // const clearValue2 = decrypt(encryptedValue, plainEncryptionKey1)?.toString(UTF8);
                }
                catch (e) {
                    console.error(`Failed to retrieve secret key: ${key}, reason: `, e);
                    throw new Error(`Failed to retrieve secret key: ${key}, reason: ${e.message}`);
                }
            }
            return config?.value;
        }
        throw new Error(`Missing envKey: ${key}. Please check AWS Parameter Store to verify if the key exists`);
    }
    encrypt(plainText) {
        if (!isEmpty(plainText)) {
            try {
                const hashDigest = sha512(this.tenantId);
                const shard = this.config(CRYPTO_SECRET_KEY);
                // console.log('========encryptionShard: ', shard);
                const encryptionKey = Base64.stringify(hmacSHA512(hashDigest, shard));
                // console.log('========encryptionKey: ', encryptionKey);
                return encrypt(plainText, encryptionKey).toString();
            }
            catch (e) {
                console.error(`Failed to encrypt: ${plainText}, reason: `, e);
            }
            throw new Error(ENCRYPTION_DECRYPTION_ERROR);
        }
        return null;
    }
    decrypt(cipherText) {
        if (!isEmpty(cipherText)) {
            try {
                const hashDigest = sha512(this.tenantId);
                const shard = this.config(CRYPTO_SECRET_KEY);
                // console.log('========encryptionShard: ', shard);
                const encryptionKey = Base64.stringify(hmacSHA512(hashDigest, shard));
                // console.log('========encryptionKey: ', encryptionKey);
                return decrypt(cipherText, encryptionKey)?.toString(Utf8);
            }
            catch (e) {
                console.error(`Failed to decrypt: ${cipherText}, reason: `, e);
            }
            throw new Error(ENCRYPTION_DECRYPTION_ERROR);
        }
        return null;
    }
}
const ConfigManager = new _ConfigManager();
Object.freeze(ConfigManager);
export default ConfigManager;
